/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable, Inject } from '@angular/core';

// Loads the Google Maps JS API on demand, using the key served by uTestV2's /api/v1/config.
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private loadPromise: Promise<void> | null = null;

  constructor(@Inject('ConfigService') private ConfigService: any) {}

  load(): Promise<void> {
    if (window.google?.maps) {
      return Promise.resolve();
    }
    if (!this.loadPromise) {
      this.loadPromise = this.injectScript().catch((error) => {
        // allow a later call to retry
        this.loadPromise = null;
        throw error;
      });
    }
    return this.loadPromise;
  }

  private async injectScript(): Promise<void> {
    const response = await this.ConfigService.config();
    const apiKey = response?.data?.google_api_key;
    if (!apiKey) {
      throw new Error('Google API key not available');
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=en-US`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        reject(new Error('Failed to load Google Maps API'));
      };
      document.head.appendChild(script);
    });
  }
}
